import { EMPTY, fromEvent } from "rxjs"
import { filter, map } from "rxjs/operators"

/**
 * Validated `BroadcastChannel` wrapper for same-origin documents (tabs, popouts,
 * iframes). Intended for module-scope singletons: the channel is never closed.
 *
 * @param name - Shared channel name. Version it (e.g. `"rt.tear-out.v1"`) so tabs
 * left open across a deploy cannot talk to newer code.
 * @param isValid - Trust boundary applied to every inbound message; anything it
 * rejects is dropped silently. Any script on the origin can post here, so check
 * every field you depend on.
 *
 * @returns `[messages$, post]`. `messages$` emits validated messages and attaches
 * a listener per subscription. A channel never receives its own posts, though
 * other channels in the same document do — this module exports one channel per
 * document, so in practice self-delivery cannot happen. `post`
 * never throws, but a message sent from a closing document may not be delivered —
 * post before `window.close()`, never after.
 *
 * Degrades to `EMPTY` and a no-op `post` where `BroadcastChannel` is unavailable
 * (older Safari, some test environments), so callers need no feature detection.
 *
 * @example
 * ```ts
 * const [pings$, postPing] = createBroadcast("app.ping.v1", isPing)
 * pings$.subscribe(() => log.debug("ping from another document"))
 * postPing({ v: 1, type: "ping" })
 * ```
 */
export function createBroadcast<T>(
  name: string,
  isValid: (value: unknown) => value is T,
) {
  const channel =
    typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(name)

  const messages$ = channel
    ? fromEvent<MessageEvent>(channel, "message").pipe(
        map((event) => event.data as unknown),
        filter(isValid),
      )
    : EMPTY

  const post = (message: T) => channel?.postMessage(message)

  return [messages$, post] as const
}
